import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { createCheckoutSession } from '../../services/api';
import './SubscriptionStyles.css';

const TIERS = [
  {
    name: 'free',
    displayName: 'Free',
    price: 0,
    priceDisplay: '$0',
    period: '/month',
    description: 'Get started with basic features',
    features: [
      '50 screener contracts/month',
      '50 watchlist contracts/month',
      '10 saved contracts',
      '5 saved parameters',
      'Basic support',
    ],
    buttonText: 'Current Plan',
    highlighted: false,
  },
  {
    name: 'essential',
    displayName: 'Essential',
    price: 9,
    priceDisplay: '$9',
    period: '/month',
    description: 'For active traders',
    features: [
      '500 screener contracts/month',
      '500 watchlist contracts/month',
      '50 saved contracts',
      '25 saved parameters',
      'Email support',
    ],
    buttonText: 'Subscribe',
    highlighted: false,
  },
  {
    name: 'plus',
    displayName: 'Plus',
    price: 24,
    priceDisplay: '$24',
    period: '/month',
    description: 'For serious traders',
    features: [
      '2,000 screener contracts/month',
      '2,000 watchlist contracts/month',
      '200 saved contracts',
      '100 saved parameters',
      'Priority support',
    ],
    buttonText: 'Subscribe',
    highlighted: true,
  },
  {
    name: 'premium',
    displayName: 'Premium',
    price: 49,
    priceDisplay: '$49',
    period: '/month',
    description: 'Unlimited everything',
    features: [
      'Unlimited screener contracts',
      'Unlimited watchlist contracts',
      'Unlimited saved contracts',
      'Unlimited saved parameters',
      'Priority support',
    ],
    buttonText: 'Subscribe',
    highlighted: false,
  },
];

function PricingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUser();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const currentTier = user?.tier || 'free';

  const handleSubscribe = async (tierName) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/pricing', tier: tierName } });
      return;
    }

    if (tierName === 'free') {
      return;
    }

    setLoading(tierName);
    setError('');

    try {
      const response = await createCheckoutSession(tierName);
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const getButtonText = (tier) => {
    if (tier.name === currentTier) {
      return 'Current Plan';
    }
    if (tier.name === 'free') {
      return 'Free Tier';
    }
    return tier.buttonText;
  };

  const isCurrentTier = (tierName) => tierName === currentTier;
  const isUpgrade = (tierName) => {
    const tierOrder = ['free', 'essential', 'plus', 'premium'];
    return tierOrder.indexOf(tierName) > tierOrder.indexOf(currentTier);
  };

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Select the plan that best fits your trading needs</p>
        {error && <div className="pricing-error">{error}</div>}
      </div>

      <div className="pricing-grid">
        {TIERS.map((tier) => (
          <div
            key={tier.name}
            className={`pricing-card ${tier.highlighted ? 'highlighted' : ''} ${
              isCurrentTier(tier.name) ? 'current' : ''
            }`}
          >
            {tier.highlighted && <div className="popular-badge">Most Popular</div>}
            {isCurrentTier(tier.name) && <div className="current-badge">Current</div>}

            <div className="pricing-card-header">
              <h2>{tier.displayName}</h2>
              <p className="tier-description">{tier.description}</p>
            </div>

            <div className="pricing-card-price">
              <span className="price">{tier.priceDisplay}</span>
              <span className="period">{tier.period}</span>
            </div>

            <ul className="pricing-features">
              {tier.features.map((feature, index) => (
                <li key={index}>
                  <span className="check-icon">&#10003;</span>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              className={`pricing-button ${isCurrentTier(tier.name) ? 'current' : ''} ${
                isUpgrade(tier.name) ? 'upgrade' : ''
              }`}
              onClick={() => handleSubscribe(tier.name)}
              disabled={isCurrentTier(tier.name) || loading === tier.name}
            >
              {loading === tier.name ? 'Processing...' : getButtonText(tier)}
            </button>
          </div>
        ))}
      </div>

      {isAuthenticated && currentTier !== 'free' && (
        <div className="manage-subscription-link">
          <p>
            Already subscribed?{' '}
            <button onClick={() => navigate('/subscription')} className="link-button">
              Manage your subscription
            </button>
          </p>
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: '2rem', padding: '0 1rem' }}>
        <p style={{ color: '#888', fontSize: '0.8rem', lineHeight: '1.6' }}>
          By subscribing, you agree to our{' '}
          <a href="/terms" style={{ color: '#218aff', textDecoration: 'underline' }}>
            Terms of Use
          </a>
          {' '}and{' '}
          <a href="/privacy" style={{ color: '#218aff', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
          . OptionsLeveling is a research tool and does not provide investment advice.
        </p>
      </div>
    </div>
  );
}

export default PricingPage;
